import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ArrowLeftIcon, ChevronDownIcon } from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import Modal from "../../Modal";
import Button from "../../Button";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import PeriodLockCell from "../PeriodLockCell";
import DepositStatusBadge from "../DepositStatusBadge";
import DepositModal from "../DepositModal";
import DepositApplyPanel from "../DepositApplyPanel";
import { ModalAlert } from "../../../ModalAlert";
import {
  addLocalNotification,
  cancelDepositUsage,
  deleteDeposit,
  forfeitDeposit,
  getDeposit,
  refreshAccountingTransaction,
  refundDeposit,
  requestListAccount,
  useAccountingDispatch,
  useAccountingState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import { formatCurrency } from "../../../../Utils";
import { getCashBankOptions } from "../../../../Utils/AccountingUtil";
import {
  getDepositActionError,
  isDepositOpen,
  isWholeFraction,
} from "../../../../Utils/DepositUtil";
import LoadErrorState from "../../LoadErrorState";
import FormError from "../../Form/FormError";

const formatDate = (value, language) =>
  value ? dayjs(value).locale(language).format("DD MMM YYYY") : "-";

const SummaryRow = ({ label, value, bold, className }) => (
  <div
    className={clsx(
      "flex justify-between text-sm",
      bold && "font-semibold",
      className,
    )}
  >
    <dt>{label}</dt>
    <dd className="tabular-nums">{value}</dd>
  </div>
);

/**
 * "Lainnya": every action stays listed. One that cannot run right now is
 * disabled with the reason written under it, so the user learns what to do
 * instead of wondering where the action went.
 */
const MoreMenu = ({ items }) => {
  const { t } = useTranslation();
  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
        {t("deposits.actions.more")}
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 w-72 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 [--anchor-gap:4px] focus:outline-none"
      >
        {items.map((item) => (
          <MenuItem key={item.label} disabled={Boolean(item.disabledReason)}>
            <button
              type="button"
              onClick={item.disabledReason ? undefined : item.onClick}
              className={clsx(
                "block w-full px-4 py-2 text-left text-sm data-[focus]:bg-gray-100",
                item.danger ? "text-red-600" : "text-gray-700",
                item.disabledReason && "cursor-not-allowed",
              )}
            >
              <span className={clsx(item.disabledReason && "opacity-50")}>
                {item.label}
              </span>
              {item.disabledReason && (
                <span className="mt-0.5 block text-xs text-gray-500">
                  {item.disabledReason}
                </span>
              )}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};

/**
 * W3 · Detail Uang Muka, with W4 (Kembalikan, Hanguskan) and W6 (Pakai di
 * Invoice) taking over the same panel. `onChanged` is called after anything
 * that changes the deposit, so the list behind can reload.
 */
const DepositDetailModal = ({
  open,
  onClose,
  depositID,
  businessID,
  onChanged,
  // "apply" opens straight on Pakai di Invoice, from the list row action.
  initialView = "detail",
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const notificationDispatch = useNotificationDispatch();
  const accountingDispatch = useAccountingDispatch();
  const { accounts, accountLoading, allAccountLoading } = useAccountingState();
  const { minDate, isDateClosed } = usePeriodLock(businessID, {
    locale: i18n.language,
  });

  const [deposit, setDeposit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // detail | refund | apply | edit. Starts on initialView, not on "detail":
  // Pakai di Invoice from a list row used to slide the detail in first and
  // then swap it for the apply panel once the deposit had loaded.
  const [view, setView] = useState(initialView);

  const [usageBusy, setUsageBusy] = useState(null);
  const [usageToCancel, setUsageToCancel] = useState(null);
  const [usageError, setUsageError] = useState("");

  const [refundDate, setRefundDate] = useState(new Date());
  const [refundAccount, setRefundAccount] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundDescription, setRefundDescription] = useState("");
  const [refundErrors, setRefundErrors] = useState({});
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState("");

  const [forfeitOpen, setForfeitOpen] = useState(false);
  const [forfeitDate, setForfeitDate] = useState(new Date());
  const [forfeitBusy, setForfeitBusy] = useState(false);
  const [forfeitError, setForfeitError] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(async () => {
    if (!depositID) return null;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getDeposit(depositID, businessID);
      setDeposit(data);
      return data;
    } catch (error) {
      setLoadError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [depositID, businessID]);

  useEffect(() => {
    if (open) {
      setView(initialView);
      setUsageError("");
      load();
    } else {
      setDeposit(null);
      setUsageToCancel(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, load]);

  useEffect(() => {
    if (view === "refund" && businessID) {
      requestListAccount(accountingDispatch, { businessID, loadAll: true });
    }
  }, [view, businessID, accountingDispatch]);

  const notify = (body) =>
    addLocalNotification(notificationDispatch, {
      id: `deposit-${body}-${Date.now()}`,
      type: "success",
      title: "common.success",
      body,
      showFull: true,
    });

  const afterChange = async (message) => {
    if (message) notify(message);
    await load();
    onChanged?.();
  };

  const remaining = Math.round(deposit?.remaining || 0);
  const usages = deposit?.usages || [];
  const used = usages.length > 0;
  const locked = isDateClosed(deposit?.createdAt);
  const hasVat = (deposit?.taxes || []).some(
    (tax) => !tax.withholding && tax.amount > 0,
  );
  const fraction = isWholeFraction(deposit?.taxBaseFraction)
    ? null
    : deposit.taxBaseFraction;
  // A deposit with no tax and no DPP Nilai Lain shows only what was received.
  const showTaxBreakdown = Boolean(fraction || deposit?.taxes?.length);

  const sumOf = (type) =>
    usages
      .filter((u) => u.type === type)
      .reduce((sum, u) => sum + (u.amount || 0), 0);
  const applied = sumOf("APPLICATION");
  const refunded = sumOf("REFUND");
  const forfeited = sumOf("FORFEIT");

  const cashBankOptions = useMemo(
    () => getCashBankOptions(accounts),
    [accounts],
  );

  const openRefund = () => {
    setRefundDate(new Date());
    setRefundAccount(null);
    setRefundAmount(String(remaining));
    setRefundDescription("");
    setRefundErrors({});
    setRefundError("");
    setView("refund");
  };

  const submitRefund = async () => {
    const amount = Math.round(Number(refundAmount) || 0);
    const errors = {};
    if (!refundDate) errors.date = "deposits.errors.date";
    if (!refundAccount?.value) errors.account = "deposits.errors.account";
    if (!(amount > 0)) errors.amount = "deposits.errors.amount";
    else if (amount > remaining)
      errors.amount = t("deposits.errors.refundAmount", {
        interpolation: { escapeValue: false },
        remaining: formatCurrency(remaining),
      });
    setRefundErrors(errors);
    if (Object.keys(errors).length || refundBusy || isDateClosed(refundDate))
      return;

    setRefundBusy(true);
    setRefundError("");
    try {
      await refundDeposit(deposit.id, {
        amount,
        account_id: refundAccount.value,
        created_at: refundDate.toISOString(),
        ...(refundDescription.trim() && {
          description: refundDescription.trim(),
        }),
      });
      setRefundBusy(false);
      setView("detail");
      await afterChange("deposits.notifications.refunded");
    } catch (error) {
      setRefundBusy(false);
      setRefundError(getDepositActionError(error, t));
    }
  };

  const submitForfeit = async () => {
    if (!forfeitDate || forfeitBusy || isDateClosed(forfeitDate)) return;
    setForfeitBusy(true);
    setForfeitError("");
    try {
      await forfeitDeposit(deposit.id, {
        created_at: forfeitDate.toISOString(),
      });
      setForfeitBusy(false);
      setForfeitOpen(false);
      await afterChange("deposits.notifications.forfeited");
    } catch (error) {
      setForfeitBusy(false);
      setForfeitError(getDepositActionError(error, t));
    }
  };

  const submitDelete = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await deleteDeposit(deposit.id);
      setDeleteBusy(false);
      setDeleteOpen(false);
      notify("deposits.notifications.deleted");
      onChanged?.();
      onClose();
    } catch (error) {
      setDeleteBusy(false);
      setDeleteError(getDepositActionError(error, t));
    }
  };

  // Using a deposit or undoing it changes the invoice's payments. The page's
  // list keeps its own copy of the invoice, and the invoice drawer prefers that
  // copy, so re-read it there too: otherwise "Dipakai di Invoice #…" opened the
  // invoice with Pembayaran Rp0 and no deposit row until the page reloaded.
  const refreshInvoices = (ids) =>
    Promise.all(
      [...new Set(ids.filter(Boolean))].map((id) =>
        refreshAccountingTransaction(accountingDispatch, id).catch(() => null),
      ),
    );

  // Undoing a use reverses its journal and moves money back onto the deposit,
  // so it is confirmed first, like Hapus.
  const cancelUsage = async () => {
    const usage = usageToCancel;
    if (!usage || usageBusy) return;
    setUsageBusy(usage.id);
    setUsageError("");
    try {
      await cancelDepositUsage(usage.id);
      setUsageBusy(null);
      setUsageToCancel(null);
      await refreshInvoices([usage.invoice?.id]);
      await afterChange("deposits.notifications.usageCancelled");
    } catch (error) {
      setUsageBusy(null);
      setUsageError(getDepositActionError(error, t));
    }
  };

  const moreItems = deposit
    ? [
        {
          label: t("deposits.actions.refund"),
          onClick: openRefund,
          disabledReason: !deposit.refundable
            ? t("deposits.disabled.taxedRefund")
            : remaining <= 0
              ? t("deposits.disabled.nothingLeft")
              : null,
        },
        {
          label: t("deposits.actions.forfeit"),
          onClick: () => {
            setForfeitDate(new Date());
            setForfeitError("");
            setForfeitOpen(true);
          },
          disabledReason:
            remaining <= 0 ? t("deposits.disabled.nothingLeft") : null,
        },
        {
          label: t("deposits.actions.edit"),
          onClick: () => setView("edit"),
          disabledReason: used
            ? t("deposits.disabled.usedEdit")
            : locked
              ? t("deposits.disabled.locked")
              : null,
        },
        {
          label: t("deposits.actions.delete"),
          danger: true,
          onClick: () => {
            setDeleteError("");
            setDeleteOpen(true);
          },
          disabledReason: used
            ? t("deposits.disabled.usedDelete")
            : locked
              ? t("deposits.disabled.locked")
              : null,
        },
      ]
    : [];

  const usageLabel = (usage) =>
    usage.type === "APPLICATION"
      ? t("deposits.detail.usage.APPLICATION", {
          interpolation: { escapeValue: false },
          number: usage.invoice?.invoiceNumber || "-",
        })
      : t(`deposits.detail.usage.${usage.type}`);

  return (
    <>
      <SlideOver
        open={
          open &&
          view === "detail" &&
          !forfeitOpen &&
          !deleteOpen &&
          !usageToCancel
        }
        onClose={onClose}
        title="deposits.detail.title"
      >
        {loadError ? (
          <LoadErrorState
            error={
              getDepositActionError(loadError, t) || "deposits.errors.loadDetail"
            }
            onRetry={load}
          />
        ) : !deposit || (loading && !deposit) ? (
          <div className="space-y-4">
            <div className="h-28 animate-pulse rounded-lg bg-gray-100" />
            <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
          </div>
        ) : (
          <div className="mb-6 space-y-6">
            <div className="rounded-lg border border-gray-100 bg-white p-4 shadow sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-500">
                    {t("deposits.fields.number")}
                  </p>
                  <h3 className="mt-1 text-2xl font-bold text-gray-900">
                    {deposit.depositNumber}
                  </h3>
                </div>
                <DepositStatusBadge
                  status={deposit.status}
                  className="text-sm"
                />
              </div>
              <p className="mt-3 text-sm text-gray-700">
                {deposit.partner?.id ? (
                  <button
                    type="button"
                    className="font-medium text-blue-600 hover:underline"
                    onClick={() => {
                      navigate(
                        `/v2/accounting/contacts?contactId=${deposit.partner.id}`,
                        {
                          state: { partner: deposit.partner },
                        },
                      );
                      onClose();
                    }}
                  >
                    {deposit.partner.name}
                  </button>
                ) : (
                  deposit.partner?.name || "-"
                )}{" "}
                · {formatDate(deposit.createdAt, i18n.language)}
                {deposit.opening && (
                  <span className="ml-2 rounded bg-gray-100 px-1.5 text-xs text-gray-600">
                    {t("deposits.detail.opening")}
                  </span>
                )}
              </p>
              {deposit.description && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">
                  {deposit.description}
                </p>
              )}

              {showTaxBreakdown && (
                <dl
                  className="mt-4 space-y-1 border-t border-gray-200 pt-4"
                  data-testid="deposit-tax-breakdown"
                >
                  <SummaryRow
                    label={t("deposits.fields.taxBase")}
                    value={formatCurrency(deposit.taxBase)}
                  />
                  {fraction && (
                    <SummaryRow
                      label={t("deposits.fields.otherTaxBase", {
                        interpolation: { escapeValue: false },
                        fraction: `${fraction.numerator}/${fraction.denominator}`,
                      })}
                      value={formatCurrency(deposit.otherTaxBase)}
                    />
                  )}
                  {(deposit.taxes || []).map((tax, index) => (
                    <SummaryRow
                      key={tax.id || index}
                      label={
                        tax.withholding
                          ? t("deposits.withheld", {
                              interpolation: { escapeValue: false },
                              name: tax.name || t("taxes.unavailable"),
                            })
                          : tax.name || t("taxes.unavailable")
                      }
                      value={`${tax.withholding ? "−" : ""}${formatCurrency(tax.amount)}`}
                      className={tax.withholding ? "text-amber-700" : undefined}
                    />
                  ))}
                </dl>
              )}

              <dl className="mt-4 space-y-1 border-t border-gray-200 pt-4">
                <SummaryRow
                  label={t("deposits.fields.received")}
                  value={formatCurrency(deposit.total)}
                />
                <SummaryRow
                  label={t("deposits.fields.used")}
                  value={`−${formatCurrency(applied)}`}
                />
                {refunded > 0 && (
                  <SummaryRow
                    label={t("deposits.fields.refunded")}
                    value={`−${formatCurrency(refunded)}`}
                  />
                )}
                {forfeited > 0 && (
                  <SummaryRow
                    label={t("deposits.fields.forfeited")}
                    value={`−${formatCurrency(forfeited)}`}
                  />
                )}
                <SummaryRow
                  label={t("deposits.fields.remaining")}
                  value={formatCurrency(remaining)}
                  bold
                  className="border-t border-gray-200 pt-2"
                />
              </dl>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <MoreMenu items={moreItems} />
                {isDepositOpen(deposit) && (
                  <Button onClick={() => setView("apply")}>
                    {t("deposits.actions.apply")}
                  </Button>
                )}
              </div>
            </div>

            <section className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow">
              <h4 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold uppercase tracking-wider text-gray-700 sm:px-6">
                {t("deposits.detail.history")}
              </h4>
              {usageError && (
                <FormError className="mx-4 mt-3 sm:mx-6" error={usageError} />
              )}
              {usages.length === 0 ? (
                <p className="px-4 py-4 text-sm text-gray-500 sm:px-6">
                  {t("deposits.detail.historyEmpty")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {usages.map((usage) => (
                    <li
                      key={usage.id}
                      className="flex items-center justify-between gap-3 px-4 py-3 text-sm sm:px-6"
                    >
                      <div className="min-w-0">
                        {usage.invoice?.id ? (
                          <button
                            type="button"
                            className="text-left font-medium text-blue-600 hover:underline"
                            onClick={() => {
                              navigate(
                                `/v2/accounting/incomes?invoiceId=${encodeURIComponent(usage.invoice.id)}`,
                              );
                              onClose();
                            }}
                          >
                            {usageLabel(usage)}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-900">
                            {usageLabel(usage)}
                          </span>
                        )}
                        <span className="block text-xs text-gray-500">
                          {formatDate(usage.date, i18n.language)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="tabular-nums text-gray-900">
                          {formatCurrency(usage.amount)}
                        </span>
                        {usage.locked ? (
                          <PeriodLockCell />
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={usageBusy === usage.id}
                            disabled={
                              Boolean(usageBusy) && usageBusy !== usage.id
                            }
                            onClick={() => {
                              setUsageError("");
                              setUsageToCancel(usage);
                            }}
                          >
                            {t("deposits.actions.cancelUsage")}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {deposit.journal?.length > 0 && (
              <section className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow">
                <h4 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold uppercase tracking-wider text-gray-700 sm:px-6">
                  {t("deposits.detail.journal")}
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">
                          {t("deposits.detail.account")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          {t("deposits.detail.debit")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          {t("deposits.detail.credit")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {deposit.journal.map((line, index) => (
                        <tr key={`${line.account?.code}-${index}`}>
                          <td className="px-4 py-2 text-gray-900">
                            <span className="font-mono text-xs text-gray-500">
                              {line.account?.code}
                            </span>{" "}
                            {line.account?.name}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                            {line.debit ? formatCurrency(line.debit) : "-"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                            {line.credit ? formatCurrency(line.credit) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </SlideOver>

      {/* W4 · Kembalikan Uang Muka */}
      <SlideOver
        open={open && view === "refund"}
        onClose={onClose}
        title="deposits.refund.title"
        actionButton={
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => setView("detail")}
          >
            <span className="sr-only">{t("common.back")}</span>
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
        }
        footer={
          <div className="flex flex-col gap-2">
            <FormError error={refundError} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setView("detail")}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={submitRefund}
                loading={refundBusy}
                disabled={isDateClosed(refundDate)}
              >
                {t("deposits.refund.submit", {
                  interpolation: { escapeValue: false },
                  amount: formatCurrency(Math.round(Number(refundAmount) || 0)),
                })}
              </Button>
            </div>
          </div>
        }
      >
        <div className="mb-6 space-y-4">
          {deposit && (
            <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-4 py-3 shadow">
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t("deposits.fields.number")}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {deposit.depositNumber}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-gray-500">
                  {t("deposits.fields.remaining")}
                </p>
                <p className="text-sm font-medium tabular-nums text-gray-900">
                  {formatCurrency(remaining)}
                </p>
              </div>
            </div>
          )}
          <FormDate
            required
            label="deposits.fields.transactionDate"
            selected={refundDate}
            onChange={setRefundDate}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeInput
            timeInputLabel={t("common.time")}
            minDate={minDate}
            isDateClosed={isDateClosed}
            error={refundErrors.date}
          />
          <FormSelect
            required
            id="deposit-refund-account"
            label="deposits.fields.refundAccount"
            searchable
            options={cashBankOptions}
            isLoading={accountLoading || allAccountLoading}
            value={refundAccount}
            onChange={setRefundAccount}
            placeholder={t("deposits.placeholders.account")}
            error={refundErrors.account}
          />
          <FormInput
            required
            id="deposit-refund-amount"
            label="deposits.fields.amount"
            value={refundAmount}
            prefix="Rp"
            inputMode="numeric"
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              decimalScale: 0,
              allowNegative: false,
              valueIsNumericString: true,
              onValueChange: ({ value }) => setRefundAmount(value),
            }}
            error={refundErrors.amount}
          />
          <FormInput
            id="deposit-refund-description"
            label="deposits.fields.description"
            value={refundDescription}
            onChange={(e) => setRefundDescription(e.target.value)}
            onClear={() => setRefundDescription("")}
            hint={t("common.optional")}
          />
        </div>
      </SlideOver>

      {/* W6 from a deposit: pick the invoice first. Rendered before the
          deposit has loaded, so opening straight on it is one panel, not the
          detail first; the invoice picker waits for the customer. */}
      {depositID && (
        <DepositApplyPanel
          open={open && view === "apply"}
          onBack={() => setView("detail")}
          onClose={onClose}
          partner={deposit?.partner}
          depositID={depositID}
          businessID={businessID}
          onApplied={async (results, invoice, { partial } = {}) => {
            if (!partial) setView("detail");
            await refreshInvoices([invoice?.id]);
            await afterChange(
              partial ? null : "deposits.notifications.applied",
            );
          }}
        />
      )}

      {deposit && (
        <DepositModal
          open={open && view === "edit"}
          onClose={() => setView("detail")}
          businessID={businessID}
          deposit={deposit}
          onSaved={async () => {
            setView("detail");
            await afterChange("deposits.notifications.updated");
          }}
        />
      )}

      {/* W4 · Hanguskan */}
      <Modal
        open={open && forfeitOpen}
        onClose={() => setForfeitOpen(false)}
        title="deposits.forfeit.title"
        footer={
          <div className="flex flex-col gap-2">
            <FormError error={forfeitError} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setForfeitOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                color="red"
                loading={forfeitBusy}
                disabled={!forfeitDate || isDateClosed(forfeitDate)}
                onClick={submitForfeit}
              >
                {t("deposits.forfeit.submit", {
                  interpolation: { escapeValue: false },
                  amount: formatCurrency(remaining),
                })}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 pb-2">
          <p className="text-sm text-gray-600">
            {t("deposits.forfeit.body", {
              interpolation: { escapeValue: false },
              amount: formatCurrency(remaining),
              partner: deposit?.partner?.name || "-",
            })}
            {hasVat && ` ${t("deposits.forfeit.vatNote")}`}
          </p>
          <FormDate
            required
            label="deposits.fields.date"
            selected={forfeitDate}
            onChange={setForfeitDate}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeInput
            timeInputLabel={t("common.time")}
            minDate={minDate}
            isDateClosed={isDateClosed}
          />
        </div>
      </Modal>

      <ModalAlert
        open={open && deleteOpen}
        setOpen={setDeleteOpen}
        title="deposits.delete.title"
        descriptionNode={
          deposit ? (
            <>
              {t("deposits.delete.body", {
                interpolation: { escapeValue: false },
                number: deposit.depositNumber,
                partner: deposit.partner?.name || "-",
                amount: formatCurrency(deposit.total),
              })}
              {deleteError && (
                <span
                  role="alert"
                  className="mt-2 block font-medium text-red-600"
                >
                  {deleteError}
                </span>
              )}
            </>
          ) : null
        }
        confirmButtonText="common.delete"
        cancelButtonText="common.cancel"
        loadingButton={deleteBusy}
        disabledButton={deleteBusy}
        onConfirm={submitDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      <ModalAlert
        open={open && Boolean(usageToCancel)}
        setOpen={(value) => !value && setUsageToCancel(null)}
        title="deposits.cancelUsage.title"
        descriptionNode={
          usageToCancel ? (
            <>
              {t("deposits.cancelUsage.body", {
                interpolation: { escapeValue: false },
                usage: usageLabel(usageToCancel),
                amount: formatCurrency(usageToCancel.amount),
                number: deposit?.depositNumber || "-",
              })}
              {usageError && (
                <span
                  role="alert"
                  className="mt-2 block font-medium text-red-600"
                >
                  {usageError}
                </span>
              )}
            </>
          ) : null
        }
        confirmButtonText="deposits.cancelUsage.confirm"
        // "Batalkan" here would read as the action itself.
        cancelButtonText="common.back"
        loadingButton={Boolean(usageBusy)}
        disabledButton={Boolean(usageBusy)}
        onConfirm={cancelUsage}
        onCancel={() => setUsageToCancel(null)}
      />
    </>
  );
};

export default DepositDetailModal;
